import React, { useRef } from 'react';

const BRAND = {
  navy: '#1A2744',
  blue: '#2563EB',
  lightBlue: '#EFF6FF',
  green: '#16A34A',
  lightGreen: '#DCFCE7',
  amber: '#D97706',
  lightAmber: '#FEF3C7',
  gray: '#6B7280',
  lightGray: '#F9FAFB',
  border: '#E5E7EB',
};

function StatCard({ label, day1, current, color, lightColor }) {
  const d1 = parseInt(day1) || 0;
  const cur = parseInt(current) || 0;
  const diff = cur - d1;
  const pct = d1 > 0 ? Math.round(((cur - d1) / d1) * 100) : null;

  return (
    <div style={{ background: lightColor, borderRadius: 12, padding: '1.25rem', border: `1px solid ${color}22` }}>
      <div style={{ fontSize: 12, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 36, fontWeight: 800, color: BRAND.navy, lineHeight: 1 }}>{cur.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: BRAND.gray, marginTop: 4 }}>Day 1: {d1.toLocaleString()}</div>
        </div>
        {pct !== null && (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: diff >= 0 ? BRAND.green : '#DC2626' }}>
              {diff >= 0 ? '+' : ''}{diff.toLocaleString()}
            </div>
            <div style={{ fontSize: 12, color: diff >= 0 ? BRAND.green : '#DC2626', fontWeight: 600 }}>
              {diff >= 0 ? '▲' : '▼'} {Math.abs(pct)}%
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BarChart({ title, data, color }) {
  const max = Math.max(...data.map(d => Math.max(d.day1, d.current)), 1);
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: BRAND.navy, marginBottom: 12 }}>{title}</div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', height: 140 }}>
        {data.map((item, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', width: '100%', justifyContent: 'center' }}>
              {/* Day 1 bar */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: BRAND.gray }}>{item.day1}</div>
                <div style={{ width: 24, background: `${color}44`, borderRadius: '4px 4px 0 0', height: `${Math.max((item.day1 / max) * 100, 4)}px` }} />
              </div>
              {/* Current bar */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: BRAND.navy }}>{item.current}</div>
                <div style={{ width: 24, background: color, borderRadius: '4px 4px 0 0', height: `${Math.max((item.current / max) * 100, 4)}px` }} />
              </div>
            </div>
            <div style={{ fontSize: 10, color: BRAND.gray, textAlign: 'center', lineHeight: 1.2 }}>{item.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 12, height: 12, background: `${color}44`, borderRadius: 2 }} />
          <span style={{ fontSize: 11, color: BRAND.gray }}>Day 1</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 12, height: 12, background: color, borderRadius: 2 }} />
          <span style={{ fontSize: 11, color: BRAND.gray }}>This month</span>
        </div>
      </div>
    </div>
  );
}

function ActionList({ title, items, color }) {
  const filtered = items.filter(i => i.trim());
  if (!filtered.length) return null;
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 8, height: 8, background: color, borderRadius: '50%' }} />
        {title}
      </div>
      {filtered.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13, color: '#374151', marginBottom: 5, alignItems: 'flex-start', paddingLeft: 16 }}>
          <span style={{ color, marginTop: 1, flexShrink: 0 }}>✓</span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
}

export default function ReportPreview({ data, summary, onBack, onSave, saving }) {
  const { clientName, reportName, keywords, traffic, conversions, actions, commentary, baseline } = data;
  const printRef = useRef();

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${reportName}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif; color: #111827; background: #fff; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .no-print { display: none !important; }
              @page { margin: 0; size: A4; }
            }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
  };

  const totalConversions =
    (parseInt(conversions.calls) || 0) +
    (parseInt(conversions.emails) || 0) +
    (parseInt(conversions.forms) || 0) +
    (parseInt(conversions.other) || 0);

  const baselineConversions =
    (parseInt(baseline.calls) || 0) +
    (parseInt(baseline.emails) || 0) +
    (parseInt(baseline.forms) || 0);

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 10 }}>
        <button className="btn btn-sm" onClick={onBack}>← Edit report</button>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-sm" onClick={onSave} disabled={saving}>
            {saving ? <><span className="spinner" /> Saving…</> : '💾 Save report'}
          </button>
          <button className="btn btn-primary" onClick={handlePrint}>🖨️ Print / Save as PDF</button>
        </div>
      </div>

      {/* Report */}
      <div ref={printRef} style={{ background: '#fff', fontFamily: "-apple-system, 'Helvetica Neue', Arial, sans-serif" }}>

        {/* Cover header */}
        <div style={{ background: BRAND.navy, padding: '2.5rem 2.5rem 2rem', borderRadius: '12px 12px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <div>
              {/* IMSR Logo placeholder */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: BRAND.blue, borderRadius: 8, padding: '6px 14px', marginBottom: 16 }}>
                <div style={{ width: 8, height: 8, background: '#60A5FA', borderRadius: '50%' }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>IMPROVE MY SEARCH RANKING</span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: 6 }}>{reportName}</div>
              <div style={{ fontSize: 16, color: '#93C5FD', fontWeight: 500 }}>{clientName}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: '#93C5FD', marginBottom: 4 }}>Report date</div>
              <div style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              {baseline.date && (
                <>
                  <div style={{ fontSize: 12, color: '#93C5FD', marginTop: 8, marginBottom: 4 }}>SEO started</div>
                  <div style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>{new Date(baseline.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                </>
              )}
            </div>
          </div>

          {/* Top-line stats in header */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {[
              { label: 'Keywords top 3', val: keywords.top3, base: baseline.top3 },
              { label: 'Keywords top 10', val: keywords.top10, base: baseline.top10 },
              { label: 'Monthly sessions', val: traffic.sessions, base: baseline.traffic },
              { label: 'Total conversions', val: totalConversions, base: baselineConversions },
            ].map((s, i) => {
              const cur = parseInt(s.val) || 0;
              const d1 = parseInt(s.base) || 0;
              const diff = cur - d1;
              return (
                <div key={i} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: '#93C5FD', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{cur.toLocaleString()}</div>
                  {d1 > 0 && (
                    <div style={{ fontSize: 11, color: diff >= 0 ? '#86EFAC' : '#FCA5A5', marginTop: 4, fontWeight: 600 }}>
                      {diff >= 0 ? '▲ +' : '▼ '}{diff.toLocaleString()} since day 1
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '2rem 2.5rem', background: '#fff', borderRadius: '0 0 12px 12px', border: `1px solid ${BRAND.border}`, borderTop: 'none' }}>

          {/* Executive summary */}
          {summary && (
            <div style={{ background: BRAND.lightBlue, borderRadius: 10, padding: '1.25rem 1.5rem', marginBottom: '2rem', borderLeft: `4px solid ${BRAND.blue}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.blue, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Executive Summary</div>
              <div style={{ fontSize: 14, color: '#1E3A5F', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{summary}</div>
            </div>
          )}

          {/* Section: Keyword performance */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: BRAND.navy, marginBottom: '1rem', paddingBottom: 8, borderBottom: `2px solid ${BRAND.blue}` }}>
              📈 Keyword Performance
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: '1.5rem' }}>
              <StatCard label="Top 3 positions" day1={baseline.top3} current={keywords.top3} color={BRAND.green} lightColor={BRAND.lightGreen} />
              <StatCard label="Top 10 (page 1)" day1={baseline.top10} current={keywords.top10} color={BRAND.blue} lightColor={BRAND.lightBlue} />
              <StatCard label="Top 20 (page 2)" day1={baseline.top20} current={keywords.top20} color={BRAND.amber} lightColor={BRAND.lightAmber} />
              <StatCard label="Position 20+" day1={baseline.top20plus} current={keywords.top20plus} color={BRAND.gray} lightColor={BRAND.lightGray} />
            </div>
            <BarChart
              title="Keyword ranking positions — Day 1 vs this month"
              color={BRAND.blue}
              data={[
                { label: 'Top 3', day1: parseInt(baseline.top3) || 0, current: parseInt(keywords.top3) || 0 },
                { label: 'Top 10', day1: parseInt(baseline.top10) || 0, current: parseInt(keywords.top10) || 0 },
                { label: 'Top 20', day1: parseInt(baseline.top20) || 0, current: parseInt(keywords.top20) || 0 },
                { label: '20+', day1: parseInt(baseline.top20plus) || 0, current: parseInt(keywords.top20plus) || 0 },
              ]}
            />
            {commentary.highlightedRankings && (
              <div style={{ background: BRAND.lightGreen, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#14532D' }}>
                <strong>Highlighted rankings:</strong> {commentary.highlightedRankings}
              </div>
            )}
          </div>

          {/* Section: Traffic */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: BRAND.navy, marginBottom: '1rem', paddingBottom: 8, borderBottom: `2px solid ${BRAND.green}` }}>
              🌐 Website Traffic
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: '1rem' }}>
              <StatCard label="Sessions" day1={baseline.traffic} current={traffic.sessions} color={BRAND.green} lightColor={BRAND.lightGreen} />
              <div style={{ background: BRAND.lightGray, borderRadius: 12, padding: '1.25rem', border: `1px solid ${BRAND.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.gray, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Users</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: BRAND.navy }}>{(parseInt(traffic.users) || 0).toLocaleString()}</div>
              </div>
              <div style={{ background: BRAND.lightGray, borderRadius: 12, padding: '1.25rem', border: `1px solid ${BRAND.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.gray, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Avg. duration</div>
                <div style={{ fontSize: 36, fontWeight: 800, color: BRAND.navy }}>{traffic.avgDuration || '—'}</div>
              </div>
            </div>
            <BarChart
              title="Website sessions — Day 1 vs this month"
              color={BRAND.green}
              data={[{ label: 'Sessions', day1: parseInt(baseline.traffic) || 0, current: parseInt(traffic.sessions) || 0 }]}
            />
            {commentary.trafficCommentary && (
              <div style={{ background: BRAND.lightGray, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#374151' }}>
                {commentary.trafficCommentary}
              </div>
            )}
          </div>

          {/* Section: Conversions */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: BRAND.navy, marginBottom: '1rem', paddingBottom: 8, borderBottom: `2px solid ${BRAND.amber}` }}>
              📞 Conversion Performance
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: '1rem' }}>
              <StatCard label="Phone calls" day1={baseline.calls} current={conversions.calls} color={BRAND.amber} lightColor={BRAND.lightAmber} />
              <StatCard label="Email enquiries" day1={baseline.emails} current={conversions.emails} color={BRAND.blue} lightColor={BRAND.lightBlue} />
              <StatCard label="Form submissions" day1={baseline.forms} current={conversions.forms} color={BRAND.green} lightColor={BRAND.lightGreen} />
              <div style={{ background: BRAND.lightGray, borderRadius: 12, padding: '1.25rem', border: `1px solid ${BRAND.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.gray, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  {conversions.otherLabel || 'Other'}
                </div>
                <div style={{ fontSize: 36, fontWeight: 800, color: BRAND.navy }}>{(parseInt(conversions.other) || 0).toLocaleString()}</div>
              </div>
            </div>
            <BarChart
              title="Conversions — Day 1 vs this month"
              color={BRAND.amber}
              data={[
                { label: 'Calls', day1: parseInt(baseline.calls) || 0, current: parseInt(conversions.calls) || 0 },
                { label: 'Emails', day1: parseInt(baseline.emails) || 0, current: parseInt(conversions.emails) || 0 },
                { label: 'Forms', day1: parseInt(baseline.forms) || 0, current: parseInt(conversions.forms) || 0 },
              ]}
            />
          </div>

          {/* Section: Actions */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: BRAND.navy, marginBottom: '1rem', paddingBottom: 8, borderBottom: `2px solid ${BRAND.gray}` }}>
              ✅ Actions Completed This Month
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <ActionList title="Technical SEO" items={actions.technical} color={BRAND.blue} />
                <ActionList title="Content produced" items={actions.content} color={BRAND.green} />
              </div>
              <div>
                <ActionList title="Backlinks obtained" items={actions.backlinks} color={BRAND.amber} />
                <ActionList title="Citations obtained" items={actions.citations} color={BRAND.navy} />
              </div>
            </div>
          </div>

          {/* Section: Next steps */}
          {(commentary.plannedContent || commentary.newIdeas) && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: BRAND.navy, marginBottom: '1rem', paddingBottom: 8, borderBottom: `2px solid ${BRAND.blue}` }}>
                🗓️ Next Month
              </div>
              {commentary.plannedContent && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.navy, marginBottom: 6 }}>Planned content</div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{commentary.plannedContent}</div>
                </div>
              )}
              {commentary.newIdeas && (
                <div style={{ background: BRAND.lightAmber, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#78350F' }}>
                  <strong>New ideas:</strong> {commentary.newIdeas}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div style={{ borderTop: `1px solid ${BRAND.border}`, paddingTop: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 12, color: BRAND.gray }}>Prepared by Improve My Search Ranking · improvemysearchranking.com</div>
            <div style={{ fontSize: 12, color: BRAND.gray }}>{reportName} · {clientName}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
